import type { HlsConfig } from 'hls.js';
import Hls from 'hls.js';

export default class CsVideoPlayerHelper {
	public readonly hls: Hls;
	readonly #defaultHlsConfig: Partial<HlsConfig> = {
		autoStartLoad: true,
		startPosition: -1,
		startLevel: 5,
		debug: false,
		capLevelOnFPSDrop: false,
		capLevelToPlayerSize: false,
		defaultAudioCodec: undefined,
		initialLiveManifestSize: 1,
		maxBufferLength: 30,
		maxMaxBufferLength: 600,
		backBufferLength: Infinity,
		frontBufferFlushThreshold: Infinity,
		maxBufferSize: 60 * 1000 * 1000,
		maxBufferHole: 0.1,
		highBufferWatchdogPeriod: 2,
		nudgeOffset: 0.1,
		nudgeMaxRetry: 3,
		maxFragLookUpTolerance: 0.25,
		liveSyncDurationCount: 3,
		liveMaxLatencyDurationCount: Infinity,
		liveDurationInfinity: false,
		preferManagedMediaSource: false,
		enableWorker: false,
		enableSoftwareAES: true,
		startFragPrefetch: false,
		testBandwidth: true,
		progressive: false,
		lowLatencyMode: true,
		fpsDroppedMonitoringPeriod: 5000,
		fpsDroppedMonitoringThreshold: 0.2,
		appendErrorMaxRetry: 3,
		enableDateRangeMetadataCues: true,
		enableID3MetadataCues: true,
		enableWebVTT: true,
		enableIMSC1: true,
		enableCEA708Captions: true,
		stretchShortVideoTrack: false,
		maxAudioFramesDrift: 10,
		forceKeyFrameOnDiscontinuity: true,
		abrEwmaFastLive: 3.0,
		abrEwmaSlowLive: 9.0,
		abrEwmaFastVoD: 3.0,
		abrEwmaSlowVoD: 9.0,
		abrEwmaDefaultEstimate: 500000,
		abrEwmaDefaultEstimateMax: 5000000,
		abrBandWidthFactor: 0.95,
		abrBandWidthUpFactor: 0.7,
		abrMaxWithRealBitrate: false,
		maxStarvationDelay: 4,
		maxLoadingDelay: 4,
		minAutoBitrate: 1,
		emeEnabled: false
	} as const;
	#config: Partial<HlsConfig>;
	#videoSourceId = 42;
	#videoKey: string | null = null;
	#autoplay: boolean = false;
	#videoPlayerEl: HTMLVideoElement;
	#videoSourceOrigin: string;
	#videoSourceBasePath: string;
	#videoUrl: string = '';
	#fallbackToHighQuality: boolean;


	constructor (
		element: HTMLVideoElement,
		videoSourceOrigin: string,
		videoSourceBasePath: string,
		startLevel: number,
		fallbackToHighQuality: boolean = true,
		debug: boolean = false,
		configOverrides: Partial<HlsConfig> = {}
	) {
		this.#config = { ...this.#defaultHlsConfig, ...configOverrides };
		this.#config.startLevel = startLevel;
		this.#config.debug = debug;
		this.#videoPlayerEl = element;
		this.#videoSourceOrigin = videoSourceOrigin;
		this.#videoSourceBasePath = videoSourceBasePath;
		this.#fallbackToHighQuality = fallbackToHighQuality;
		this.hls = new Hls(this.#config);
	}

	public loadVideo(videoKey: string, autoplay: boolean, videoSourceId: number = 42): void {
		this.#videoSourceId = videoSourceId;
		this.#videoKey = videoKey;
		this.#autoplay = autoplay;
		this.#videoUrl = this.getVideoUrl();
		if(this.hlsVideoIsSupported()) {
			this.hls.loadSource(this.#videoUrl);
			this.hls.attachMedia(this.#videoPlayerEl);
			if(this.#autoplay) {
				this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
					this.#videoPlayerEl.play();
				});
			}
			this.hls.on(Hls.Events.ERROR, (_event, data) => {
				if(data.details === Hls.ErrorDetails.MANIFEST_PARSING_ERROR) { // happens for old vidly videos that don't have an hls.m3u8 file
					// fallback to direct play
					this.hls.detachMedia();
					this.#videoPlayerEl.src = this.#videoUrl;
				}
			});
		} else {
			this.#videoPlayerEl.src = this.#videoUrl;
			if(this.#autoplay) {
				this.#videoPlayerEl.play();
			}
		}
	}

	public hlsVideoIsSupported(): boolean {
		return Hls.isSupported(); // will return false for iOS before 17.1 on iPhones
	}

	public getVideoUrl(): string {
		// need to check for canPlayType 'application/vnd.apple.mpegurl' for iPhones running iOS 16.x and 17.0
		const quality =  this.hlsVideoIsSupported() || this.#videoPlayerEl.canPlayType('application/vnd.apple.mpegurl') ? 'adaptive'
			: (this.#fallbackToHighQuality ? '1080' : '480');
		return `${this.#videoSourceOrigin}/${this.#videoSourceBasePath}/${this.#videoKey}?source=${this.#videoSourceId}&size=${quality}`;
	}
}
